/**
 * Utilities for working with Descope user roles and tenants
 *
 * Descope stores roles in the access token, so we can read them directly
 * without making additional API calls.
 *
 * PERFORMANCE NOTE:
 * - useUser() from @descope/react-sdk reads from the decoded JWT token in memory
 * - NO network requests are made when checking roles
 * - The token is already cached after login (localStorage/sessionStorage)
 * - This is extremely fast - just reading from memory and parsing JSON
 */

import { type UserResponse } from '@descope/core-js-sdk';
import { helpers } from '@kirin/api-client/contracts';

type TenantPermissions = helpers.TenantPermissions;

/**
 * Descope UserTenant type (not exported from core-js-sdk)
 */
type UserTenant = {
  tenantId: string;
  roleNames?: string[];
  tenantName: string;
};

/**
 * Descope user structure (from useUser hook)
 * This is what comes from the Descope SDK
 */
export type DescopeUser = UserResponse;

/**
 * Our normalized auth context format
 */
export interface AuthContext {
  globalRoles: string[];
  tenants: Record<string, TenantPermissions>;
}

/**
 * Transform Descope user data into our normalized auth context format
 *
 * This transformation is cached via useMemo in useRoleCheck hook.
 * The function itself is pure and has no side effects.
 *
 * @param descopeUser - User object from Descope's useUser() hook
 * @returns Normalized auth context with global roles and tenants
 *
 * @example
 * const { user } = useUser();
 * const authContext = getAuthContextFromDescope(user);
 * // authContext.globalRoles = ['user']
 * // authContext.tenants = { 'T34...': { roles: ['owner'], permissions: [] } }
 */
export function getAuthContextFromDescope(
  descopeUser: DescopeUser | null | undefined
): AuthContext {
  if (!descopeUser) {
    return {
      globalRoles: [],
      tenants: {},
    };
  }

  // Extract global roles
  const globalRoles = Array.isArray(descopeUser.roleNames)
    ? descopeUser.roleNames
    : [];

  // Transform userTenants array into our tenants record
  const tenants: Record<string, TenantPermissions> = {};

  if (Array.isArray(descopeUser.userTenants)) {
    descopeUser.userTenants.forEach((tenant: UserTenant) => {
      tenants[tenant.tenantId] = {
        roles: tenant.roleNames || [],
        permissions: [], // Descope SDK's UserTenant doesn't include permissions in the access token
      };
    });
  }

  return {
    globalRoles,
    tenants,
  };
}
